import React, { useState } from 'react'
import { SyntheticAthlete } from './syntheticDashboardData'
import { CheckCircle2, AlertTriangle, HelpCircle, XCircle, ChevronRight } from 'lucide-react'

interface AvailabilitySegmentedBarProps {
  athletes: SyntheticAthlete[]
  onSelectAthlete?: (athlete: SyntheticAthlete) => void
  compact?: boolean
}

type AvailabilityCategory = 'all' | 'available_100' | 'limited' | 'needs_assessment' | 'unavailable' | 'not_assessed'

export const AvailabilitySegmentedBar: React.FC<AvailabilitySegmentedBarProps> = ({
  athletes,
  onSelectAthlete,
  compact = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<AvailabilityCategory>('all')

  const total = athletes.length

  const counts = {
    available_100: athletes.filter((a) => a.availability === 'available_100').length,
    limited: athletes.filter((a) => a.availability === 'limited').length,
    needs_assessment: athletes.filter((a) => a.availability === 'needs_assessment').length,
    unavailable: athletes.filter((a) => a.availability === 'unavailable').length,
    not_assessed: athletes.filter((a) => a.availability === 'not_assessed').length,
  }

  const pcts = {
    available_100: total > 0 ? (counts.available_100 / total) * 100 : 0,
    limited: total > 0 ? (counts.limited / total) * 100 : 0,
    needs_assessment: total > 0 ? (counts.needs_assessment / total) * 100 : 0,
    unavailable: total > 0 ? (counts.unavailable / total) * 100 : 0,
    not_assessed: total > 0 ? (counts.not_assessed / total) * 100 : 0,
  }

  const filteredAthletes = selectedCategory === 'all'
    ? athletes
    : athletes.filter((a) => a.availability === selectedCategory)

  const categories = [
    {
      id: 'available_100' as const,
      label: 'Disponible 100%',
      count: counts.available_100,
      pct: pcts.available_100,
      color: '#15803d',
      bg: '#dcfce7',
      border: '#bbf7d0',
      icon: CheckCircle2,
    },
    {
      id: 'limited' as const,
      label: 'Con restricciones',
      count: counts.limited,
      pct: pcts.limited,
      color: '#b45309',
      bg: '#fef3c7',
      border: '#fde68a',
      icon: AlertTriangle,
    },
    {
      id: 'needs_assessment' as const,
      label: 'Pendiente valoración',
      count: counts.needs_assessment,
      pct: pcts.needs_assessment,
      color: '#1d4ed8',
      bg: '#dbeafe',
      border: '#bfdbfe',
      icon: HelpCircle,
    },
    {
      id: 'unavailable' as const,
      label: 'No disponible',
      count: counts.unavailable,
      pct: pcts.unavailable,
      color: '#b91c1c',
      bg: '#fee2e2',
      border: '#fecaca',
      icon: XCircle,
    },
    {
      id: 'not_assessed' as const,
      label: 'Sin valorar',
      count: counts.not_assessed,
      pct: pcts.not_assessed,
      color: '#64748b',
      bg: '#f1f5f9',
      border: '#e2e8f0',
      icon: HelpCircle,
    },
  ]

  return (
    <div className="availability-card" style={{
      background: 'var(--surface-card, #ffffff)',
      border: '1px solid var(--surface-border, #e2e8f0)',
      borderRadius: '8px',
      padding: compact ? '1rem' : '1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.85rem' }}>
        <div>
          <div style={{
            fontSize: compact ? '0.75rem' : '0.8rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted, #64748b)',
          }}>
            Disponibilidad Operativa del Grupo
          </div>
          <div style={{
            fontSize: compact ? '1.1rem' : '1.35rem',
            fontWeight: 800,
            color: 'var(--text-main, #0f172a)',
            fontVariantNumeric: 'tabular-nums',
            marginTop: '2px',
          }}>
            {counts.available_100} de {total} <span style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--text-secondary, #475569)' }}>aptos sin restricción ({Math.round(pcts.available_100)}%)</span>
          </div>
        </div>

        {selectedCategory !== 'all' && (
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            style={{
              background: 'transparent',
              border: '1px solid var(--surface-border-strong, #cbd5e1)',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '0.75rem',
              color: 'var(--text-secondary, #475569)',
              cursor: 'pointer',
            }}
          >
            Ver todos ({total})
          </button>
        )}
      </div>

      {/* Segmented Progress Bar */}
      <div
        role="progressbar"
        aria-label="Distribución de disponibilidad"
        style={{
          display: 'flex',
          height: compact ? '14px' : '18px',
          borderRadius: '999px',
          overflow: 'hidden',
          backgroundColor: '#e2e8f0',
          marginBottom: '0.9rem',
          cursor: 'pointer',
        }}
      >
        {categories.map((cat) => {
          if (cat.count === 0) return null
          const isSelected = selectedCategory === cat.id
          return (
            <div
              key={cat.id}
              onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
              title={`${cat.label}: ${cat.count} (${cat.pct.toFixed(1)}%)`}
              style={{
                width: `${cat.pct}%`,
                backgroundColor: cat.color,
                transition: 'opacity 0.15s ease',
                opacity: selectedCategory === 'all' || isSelected ? 1 : 0.35,
                borderRight: '2px solid #ffffff',
              }}
            />
          )
        })}
      </div>

      {/* Legend & Filter Chips */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: compact ? '6px' : '8px',
        marginBottom: '1rem',
      }}>
        {categories.map((cat) => {
          const isSelected = selectedCategory === cat.id
          const Icon = cat.icon
          return (
            <button
              key={cat.id}
              type="button"
              onClick={() => setSelectedCategory(isSelected ? 'all' : cat.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: compact ? '4px 8px' : '6px 10px',
                borderRadius: '6px',
                border: `1px solid ${isSelected ? cat.color : cat.border}`,
                background: isSelected ? cat.bg : '#ffffff',
                color: cat.color,
                fontSize: compact ? '0.75rem' : '0.8rem',
                fontWeight: isSelected ? 700 : 600,
                cursor: 'pointer',
                fontVariantNumeric: 'tabular-nums',
                boxShadow: isSelected ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <Icon size={13} strokeWidth={2.5} />
              <span>{cat.label}</span>
              <span style={{
                background: cat.color,
                color: '#ffffff',
                borderRadius: '10px',
                padding: '1px 6px',
                fontSize: '0.7rem',
                fontWeight: 700,
              }}>
                {cat.count}
              </span>
            </button>
          )
        })}
      </div>

      {/* Drilldown List for Non-All or Highlighted */}
      {selectedCategory !== 'all' && (
        <div style={{
          borderTop: '1px solid var(--surface-border, #e2e8f0)',
          paddingTop: '0.75rem',
        }}>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            color: 'var(--text-secondary, #475569)',
            marginBottom: '0.5rem',
          }}>
            Atletas en categoría: {categories.find((c) => c.id === selectedCategory)?.label} ({filteredAthletes.length})
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '8px',
          }}>
            {filteredAthletes.map((ath) => (
              <div
                key={ath.id}
                onClick={() => onSelectAthlete && onSelectAthlete(ath)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid var(--surface-border, #e2e8f0)',
                  background: 'var(--surface-subtle, #f8fafc)',
                  cursor: onSelectAthlete ? 'pointer' : 'default',
                  transition: 'background 0.15s ease, border-color 0.15s ease',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '26px',
                    height: '26px',
                    borderRadius: '4px',
                    background: 'var(--nav-bg-base, #0a1320)',
                    color: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 800,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    #{ath.dorsal}
                  </span>
                  <div>
                    <div style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                      {ath.name}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted, #64748b)' }}>
                      {ath.position} · {ath.unit} · {ath.club}
                    </div>
                  </div>
                </div>

                {ath.availabilityNote && (
                  <span
                    title={ath.availabilityNote}
                    style={{
                      fontSize: '0.7rem',
                      color: '#b45309',
                      maxWidth: '120px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {ath.availabilityNote}
                  </span>
                )}

                {onSelectAthlete && (
                  <ChevronRight size={14} color="var(--text-muted, #64748b)" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
